import { useState } from 'react';
import useSWR from 'swr';
import classNames from 'classnames';
import { Typography } from '@johnlewispartnership/wtr-ingredients/dist/foundations/typography';

import recipesService, { defaultSummaryRequestBody } from 'services/recipes';
import type {
  RecipeFilterGroup,
  SummaryPreferences,
  SummaryResponse,
} from 'services/recipes/types';
import { useSearchParams } from 'renderer/hooks/useSearchParams';
import { queryToFilters } from 'renderer/utils/queryToFilters';

import {
  RECIPE_FEATURED_LAST_PUBLISHED,
  RECIPE_QUERY_PAGE,
  RECIPE_QUERY_SEARCH_TERM,
  RECIPE_QUERY_SORT_BY,
} from '../constants';
import mergeFilters from '../utils/mergeFilters';
import { recipesSummaryCacheKey } from '../utils/recipesSummaryCacheKey';
import RecipeListingPageLayout from '../RecipeListingPageLayout';
import RecipeList from '../RecipeList';
import RecipeSortBy from '../RecipeSortBy';
import RecipeFilters from '../RecipeFilters';

import styles from './RecipeListingPage.module.scss';

type RecipeListingPageProps = {
  dietaryFilters: RecipeFilterGroup[];
  disableFilters: boolean;
  disableSortBy: boolean;
  gridSubtitle: string;
  gridTitle: string;
  gridType: 'recipe-cards' | 'featured';
  defaultSortBy: typeof RECIPE_FEATURED_LAST_PUBLISHED;
  preloadImages: boolean;
};

const fallbackData: SummaryResponse = { recipes: [], filters: [], totalMatches: 0 };

const getStatusMessage = ({
  error,
  hasNoSearchResults,
  hasSearchResults,
  searchTerm,
  totalMatches,
}: {
  error: boolean;
  hasNoSearchResults: boolean;
  hasSearchResults: boolean;
  searchTerm?: string;
  totalMatches?: number;
}) => {
  if (error) {
    return (
      <Typography styleAs="headingLarge">Sorry, something went wrong loading recipes</Typography>
    );
  }

  if (hasNoSearchResults) {
    return (
      <>
        <Typography styleAs="headingLarge">
          Sorry, we couldn't find anything for "{searchTerm}"
        </Typography>
        <Typography styleAs="paragraph">Try entering a different search term</Typography>
      </>
    );
  }

  if (hasSearchResults) {
    return (
      <span className={styles.srOnly}>
        {totalMatches} recipes found {searchTerm ? `for search term "${searchTerm}"` : ''}
      </span>
    );
  }

  return null;
};

const RecipeListingPage = ({
  gridType,
  disableFilters,
  disableSortBy,
  preloadImages,
}: RecipeListingPageProps) => {
  const [searchParams] = useSearchParams();

  const searchTerm = searchParams.get(RECIPE_QUERY_SEARCH_TERM) ?? '';

  const [mergedFilters, setMergedFilters] = useState({
    searchTerm,
    filters: fallbackData.filters,
  });

  const sortBy = searchParams.get(RECIPE_QUERY_SORT_BY) ?? '';
  const pageNumber = Number(searchParams.get(RECIPE_QUERY_PAGE) ?? 1) || 1;

  const filtersParam = queryToFilters(searchParams);

  const fetchRecipesSummary = (): Promise<SummaryResponse> => {
    const size = defaultSummaryRequestBody.preferences.size;
    const start = (pageNumber - 1) * size;

    const preferences: SummaryPreferences = {
      ...defaultSummaryRequestBody.preferences,
      start,
      ...(sortBy && { sortBy }),
    };

    return recipesService.postSummary({
      body: {
        ...(searchTerm && { search: { term: searchTerm, tags: [[]] } }),
        ...(filtersParam.length && { filters: filtersParam }),
        preferences,
      },
    });
  };

  const { data, isLoading, isValidating, error } = useSWR(
    recipesSummaryCacheKey(searchTerm, sortBy, pageNumber, filtersParam),
    fetchRecipesSummary,
    {
      fallbackData,
      onSuccess: response => {
        setMergedFilters(prev => {
          const isNewSearchTerm = prev.searchTerm !== searchTerm;

          return {
            searchTerm,
            filters: isNewSearchTerm
              ? response.filters
              : mergeFilters(prev.filters, response.filters),
          };
        });
      },
    },
  );

  const { filters } = mergedFilters;
  const { recipes } = data;

  const hasNoSearchResults = !isLoading && !isValidating && !!searchTerm && data.totalMatches === 0;
  const hasSearchResults = !isLoading && !isValidating && !!data.totalMatches;
  const hasPageLevelError = hasNoSearchResults || !!error;
  const shouldShowLayout = !isLoading && !hasPageLevelError;

  const statusComponent = getStatusMessage({
    error: !!error,
    hasNoSearchResults,
    hasSearchResults,
    searchTerm,
    totalMatches: data.totalMatches,
  });

  return (
    <>
      <div
        aria-live="polite"
        aria-atomic="true"
        role="status"
        className={classNames({ [styles.statusContainer]: hasPageLevelError })}
      >
        {statusComponent}
      </div>
      {shouldShowLayout && (
        <RecipeListingPageLayout
          gridType={gridType}
          disableFilters={disableFilters}
          disableSortBy={disableSortBy}
          preloadImages={preloadImages}
          filterComponent={<RecipeFilters filters={filters} />}
          sortByComponent={<RecipeSortBy />}
          recipeCardGrid={<RecipeList recipes={recipes} />}
        />
      )}
    </>
  );
};

export default RecipeListingPage;
